/**
 * Utility functions for handling dates without timezone issues
 */

/**
 * Parse an ISO date string (YYYY-MM-DD) to a Date object in local timezone
 * This avoids the timezone conversion issue that occurs when using new Date() with ISO date strings
 * 
 * @param {string} dateString - ISO date string in format YYYY-MM-DD
 * @returns {Date} Date object in local timezone, or null if invalid
 */
export const parseLocalDate = (dateString) => {
  if (!dateString) return null;
  
  // Split the date string into components
  const parts = dateString.split('-');
  
  // Validate that we have exactly 3 parts
  if (parts.length !== 3) return null;
  
  const [year, month, day] = parts.map(Number);
  
  // Validate that all parts are valid numbers
  if (isNaN(year) || isNaN(month) || isNaN(day)) return null;
  
  // Create a Date object in local timezone (month is 0-indexed)
  return new Date(year, month - 1, day);
};

/**
 * Format a date string (YYYY-MM-DD) to localized date format
 * This function parses the date without timezone conversion before formatting
 * 
 * @param {string} dateString - ISO date string in format YYYY-MM-DD
 * @param {string} locale - Locale for formatting (default: 'es-ES')
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string
 */
export const formatLocalDate = (dateString, locale = 'es-ES', options = {}) => {
  const date = parseLocalDate(dateString);
  if (!date) return '';
  
  return date.toLocaleDateString(locale, options);
};

/**
 * Format a datetime string to localized datetime format
 * 
 * @param {string} dateTimeString - ISO datetime string
 * @param {string} locale - Locale for formatting (default: 'es-ES')
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted datetime string
 */
export const formatLocalDateTime = (dateTimeString, locale = 'es-ES', options = {}) => {
  if (!dateTimeString) return '';
  
  const date = new Date(dateTimeString);
  return date.toLocaleString(locale, options);
};
